import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Lange Listen ohne Blättern: zuerst `schritt` Einträge, weitere lädt ein unsichtbarer Anker am
 * Listenende still nach, sobald er beim Scrollen in Sicht kommt. Die Daten sind ohnehin geladen –
 * das Stückeln hält nur bei tausenden Zeilen das Rendern flüssig; bei ein paar hundert merkt man es
 * nicht.
 *
 * Ändert sich die Liste (Suche, Filter), geht es wieder mit dem ersten Stück los. `zeigeBis(i)`
 * stellt sicher, dass Eintrag i gerendert ist (z. B. ein gerade angelegter, der aufleuchten soll).
 */
export function useStueckweise<T>(liste: T[], schritt = 100) {
  const [anzahl, setAnzahl] = useState(schritt);
  const vorige = useRef(liste);
  if (vorige.current !== liste) {
    // Neue Liste: nicht per Effekt zurücksetzen, sonst blitzt einmal die alte Länge auf.
    vorige.current = liste;
    if (anzahl !== schritt) setAnzahl(schritt);
  }

  const anker = useRef<HTMLDivElement | null>(null);
  const mehr = useCallback(() => setAnzahl(a => (a < liste.length ? a + schritt : a)), [liste.length, schritt]);

  useEffect(() => {
    const el = anker.current;
    if (!el || anzahl >= liste.length) return;
    const beobachter = new IntersectionObserver(e => { if (e[0].isIntersecting) mehr(); }, { rootMargin: '600px' });
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, [anzahl, liste.length, mehr]);

  const zeigeBis = useCallback((index: number) => {
    setAnzahl(a => Math.max(a, Math.ceil((index + 1) / schritt) * schritt));
  }, [schritt]);

  return {
    sichtbar: liste.slice(0, anzahl),
    /** Ans Listenende setzen – lädt beim Hineinscrollen nach. */
    Ende: anzahl < liste.length ? <div ref={anker} aria-hidden className="h-px" /> : null,
    zeigeBis,
  };
}
